import { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardService } from '../services/dashboard';

export type RecipeExecRow = { id: number; equipment: string; recipe: string; count: number };

type Props = {
  scopeType: 'group' | 'location';
  scopeId?: number;
};

const PAGE_LIMIT = 10;

export const useRecipeDashboardStats = ({ scopeType, scopeId }: Props) => {
  const [recipesData, setRecipesData] = useState<RecipeExecRow[]>([]);
  const [usageSummary, setUsageSummary] = useState<Record<string, number>>({});

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setRecipesData([]);
    setUsageSummary({});
    setPage(1);
    setHasMore(true);
  }, [scopeType, scopeId]);

  useEffect(() => {
    if (!scopeId) return;
    if (!hasMore && page > 1) return;

    let active = true;

    const fetchStats = async () => {
      setLoading(true);
      loadingRef.current = true;

      try {
        const apiScopeType = scopeType === 'group' ? 'STORE' : 'LOCATION';
        
        const response = await DashboardService.getRecipeStats({ 
          scopeType: apiScopeType, 
          scopeId,
          page,
          limit: PAGE_LIMIT
        });

        if (active) {
          if (page === 1) {
            setUsageSummary(response.usageSummary);
          }
  
          const newItems = response.recipeStats || [];
  
          const mappedNewItens = newItems.map(item => ({
            id: item.id,
            equipment: item.equipmentName,
            recipe: item.recipeName,
            count: item.count
          }));
  
          setRecipesData(prev => {
            if (page === 1) return mappedNewItens;
            const newUnique = mappedNewItens.filter(n => !prev.some(p => p.id === n.id));
            return [...prev, ...newUnique]
          });
  
          const currentCount = (page - 1) * PAGE_LIMIT + newItems.length;
          setHasMore(currentCount < response.total);
        }
      } catch (err) {
        if (active) {
          console.error(err);
          setError('Failed to load recipe stats');
        }
      } finally {
        if (active) {
          setLoading(false);
          loadingRef.current = false;
        }
      }
    };

    fetchStats();

    return () => { active = false };
  }, [scopeType, scopeId, page]);

  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore) {
      setPage(prev => prev + 1);
    }
  }, [hasMore]);

  return {
    recipesData,
    usageSummary,
    loading,
    hasMore,
    loadMore,
    error,
    refresh: useCallback(() => {
      setPage(1);
      setHasMore(true);
    }, [])
  }
};